import {
  CATALOG_TAGS_MAX,
  ITEM_VARIANTS_MAX,
  PRICE_CENTS_MAX,
  catalogNameSchema,
  parseARS,
  type CreateItemInput,
  type ItemDto,
  type UpdateItemInput,
} from '@gl/shared';
import { z } from 'zod';

const moneyText = (required: boolean) =>
  z
    .string()
    .trim()
    .superRefine((s, ctx) => {
      if (!s) {
        if (required) ctx.addIssue({ code: 'custom', message: 'Ingresá un precio' });
        return;
      }
      const cents = parseARS(s);
      if (cents === null) ctx.addIssue({ code: 'custom', message: 'Precio inválido' });
      else if (cents < 0) ctx.addIssue({ code: 'custom', message: 'No puede ser negativo' });
      else if (cents > PRICE_CENTS_MAX) ctx.addIssue({ code: 'custom', message: 'Es demasiado alto' });
    });

export function splitTags(s: string): string[] {
  return s
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

const variantFormSchema = z.object({
  id: z.string().optional(),
  name: catalogNameSchema,
  price: moneyText(true),
  isAvailable: z.boolean(),
});

/** Form del item: precios como texto en pesos ("1.500", "1500,50"). */
export const itemFormSchema = z
  .object({
    name: catalogNameSchema,
    description: z.string().trim().max(500, 'Máximo 500 caracteres'),
    categoryId: z.string().min(1, 'Elegí una categoría'),
    price: moneyText(false),
    cost: moneyText(false),
    imageUrl: z.url('URL inválida').or(z.literal('')),
    tags: z
      .string()
      .refine((s) => splitTags(s).length <= CATALOG_TAGS_MAX, `Máximo ${CATALOG_TAGS_MAX} etiquetas`)
      .refine((s) => splitTags(s).every((t) => t.length <= 30), 'Cada etiqueta puede tener hasta 30 caracteres'),
    isAvailable: z.boolean(),
    variants: z.array(variantFormSchema).max(ITEM_VARIANTS_MAX, `Máximo ${ITEM_VARIANTS_MAX} variantes`),
  })
  .superRefine((v, ctx) => {
    if (v.variants.length === 0 && !v.price) {
      ctx.addIssue({ code: 'custom', path: ['price'], message: 'Ingresá un precio (o agregá variantes)' });
    }
  });

export type ItemFormValues = z.input<typeof itemFormSchema>;

export function centsToInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return (cents / 100).toString().replace('.', ',');
}

export function itemToForm(item: ItemDto | null, categoryId: string): ItemFormValues {
  return {
    name: item?.name ?? '',
    description: item?.description ?? '',
    categoryId: item?.categoryId ?? categoryId,
    price: item ? (item.variants.length && !item.priceCents ? '' : centsToInput(item.priceCents)) : '',
    cost: centsToInput(item?.costCents),
    imageUrl: item?.imageUrl ?? '',
    tags: item?.tags.join(', ') ?? '',
    isAvailable: item?.isAvailable ?? true,
    variants: (item?.variants ?? []).map((v) => ({
      id: v.id,
      name: v.name,
      price: centsToInput(v.priceCents),
      isAvailable: v.isAvailable,
    })),
  };
}

const toCents = (s: string) => (s.trim() ? (parseARS(s) ?? 0) : null);

export function formToCreate(v: ItemFormValues): CreateItemInput {
  return {
    categoryId: v.categoryId,
    name: v.name.trim(),
    description: v.description.trim() || null,
    imageUrl: v.imageUrl || null,
    priceCents: toCents(v.price) ?? 0,
    costCents: toCents(v.cost),
    tags: splitTags(v.tags),
    isAvailable: v.isAvailable,
    variants: v.variants.map((vr, i) => ({
      name: vr.name.trim(),
      priceCents: toCents(vr.price) ?? 0,
      isAvailable: vr.isAvailable,
      sortOrder: i,
    })),
  };
}

export function formToUpdate(v: ItemFormValues): UpdateItemInput {
  const { variants, ...rest } = formToCreate(v);
  return {
    ...rest,
    variants: v.variants.map((vr, i) => ({
      ...(vr.id ? { id: vr.id } : {}),
      name: vr.name.trim(),
      priceCents: toCents(vr.price) ?? 0,
      isAvailable: vr.isAvailable,
      sortOrder: variants?.[i]?.sortOrder ?? i,
    })),
  };
}
